/**
 * Responsive multi-line area chart for metrics tabs.
 * Wraps Recharts AreaChart with consistent styling.
 */

import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type LineConfig = {
  readonly key: string;
  readonly color: string;
  readonly label: string;
};

type Props = {
  readonly data: ReadonlyArray<Record<string, unknown>>;
  readonly lines: ReadonlyArray<LineConfig>;
  readonly xKey: string;
  readonly height?: number;
};

const AXIS_STYLE = { fontSize: 12 } as const;

const TOOLTIP_STYLE: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid var(--color-border)",
  borderRadius: 6,
  padding: "8px 12px",
  fontSize: 13,
};

export function TrendLineChart({
  data,
  lines,
  xKey,
  height,
}: Props): React.JSX.Element {
  const chartHeight = height ?? 280;

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <AreaChart data={data as Record<string, unknown>[]}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xKey} tick={AXIS_STYLE} />
        <YAxis tick={AXIS_STYLE} />
        <Tooltip contentStyle={TOOLTIP_STYLE} />
        <Legend verticalAlign="bottom" />
        {lines.map((line) => (
          <Area
            key={line.key}
            type="monotone"
            dataKey={line.key}
            name={line.label}
            stroke={line.color}
            fill={line.color}
            strokeWidth={2}
            fillOpacity={0.1}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}
